import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { AtSign, CheckCircle } from 'lucide-react';

export default function SetUsernamePage() {
  const { currentTheme } = useTheme();
  const { setUsername, user } = useAuth();
  const navigate = useNavigate();
  
  const [username, setUsernameValue] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      toast.error('Username can only contain letters, numbers, and underscores');
      return;
    }

    setLoading(true);
    
    try {
      await setUsername(username);
      toast.success('Username set successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Username already taken');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: currentTheme.background }}
    >
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent})` }}
          >
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="font-display text-3xl md:text-4xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
            Welcome, {user?.name}!
          </h1>
          <p className="font-body text-lg opacity-70">
            Choose a unique username to complete your profile
          </p>
        </div>

        <div 
          className="p-8 rounded-2xl"
          style={{ backgroundColor: `${currentTheme.muted}50` }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="font-body font-medium">Username</Label>
              <div className="relative">
                <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-40" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Choose your username"
                  value={username}
                  onChange={(e) => setUsernameValue(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  className="pl-12 h-12 input-focus"
                  required
                  data-testid="set-username-input"
                />
              </div>
              <p className="text-xs opacity-60 font-body">
                Only letters, numbers, and underscores. Minimum 3 characters.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-12 font-body font-medium"
              style={{ backgroundColor: currentTheme.primary }}
              disabled={loading || username.length < 3}
              data-testid="set-username-submit"
            >
              {loading ? 'Setting username...' : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
